/**
 * Book length — Standard (12 story pages) vs Extended (28 story pages).
 *
 * Customer-facing counts include the front + back cover, so Standard is sold as a
 * "14-page" book and Extended as a "30-page" one. Everything server-side counts INNER
 * (story) pages only — `ProductRun.generatedImages` never holds the covers.
 *
 * How the two editions share one story (see src/lib/comix/extended-edition.ts): the
 * preview only ever renders the first sharedPreviewPages() pages of the standard 12-page
 * script, and those pages are identical in both editions. Picking Extended at checkout
 * builds a longer telling of the rest after purchase (generate-more), so the preview the
 * customer saw is the opening of whichever book they bought.
 *
 * Safe for client and server (no Node-only imports).
 */

export type BookPageCount = 12 | 28;

/**
 * Feature flag for the whole 14/30-page choice — UI (length picker, bag length line, cart
 * attributes) and server (extended prepare + length-switch routes, order-paid stamping
 * innerPageCount). Off = every book is the standard one, exactly as before this feature.
 * NEXT_PUBLIC_ so the same value is readable on the client and the server.
 */
export const EXTENDED_BOOK_FLAG = process.env.NEXT_PUBLIC_COMIX_EXTENDED_BOOK === 'true';

export const STANDARD_INNER_PAGES: BookPageCount = 12;
export const EXTENDED_INNER_PAGES: BookPageCount = 28;

/** Flat price difference for the Extended edition, per format. Display only — the real price is
 *  whatever the extended Shopify variant costs, so keep these equal to (extended − standard):
 *  digital $25 → $35, printed $40 → $55, post-purchase printed add-on $28 → $38. */
export const EXTENDED_UPCHARGE_DIGITAL_USD = 10;
export const EXTENDED_UPCHARGE_PRINTED_USD = 15;
export const EXTENDED_UPCHARGE_UPSELL_USD = 10;
/** @deprecated the digital figure — use extendedUpcharge(format). */
export const EXTENDED_UPCHARGE_USD = EXTENDED_UPCHARGE_DIGITAL_USD;

export type BookFormat = 'digital' | 'printed' | 'upsell-printed';

export function extendedUpcharge(format: BookFormat = 'digital'): number {
  if (format === 'printed') return EXTENDED_UPCHARGE_PRINTED_USD;
  if (format === 'upsell-printed') return EXTENDED_UPCHARGE_UPSELL_USD;
  return EXTENDED_UPCHARGE_DIGITAL_USD;
}

/** Cart line attribute carrying the picked length (hidden — underscore prefix). The
 *  order-paid webhook reads it to decide how many pages generate-more renders. */
export const PAGE_COUNT_ATTR = '_pageCount';
/** Visible line attribute so the length also shows in Shopify checkout + the order admin. */
export const PAGE_COUNT_VISIBLE_ATTR = 'Book length';

export function normalizePageCount(value: unknown): BookPageCount {
  return Number(value) === EXTENDED_INNER_PAGES ? EXTENDED_INNER_PAGES : STANDARD_INNER_PAGES;
}

/** Customer-facing page count — inner pages + front & back cover. */
export function displayPageCount(pageCount: BookPageCount): number {
  return pageCount + 2;
}

/** The two customer-facing lengths (14 / 30) — for UI that names a length before there's a line. */
export const STANDARD_DISPLAY_PAGES = displayPageCount(STANDARD_INNER_PAGES);
export const EXTENDED_DISPLAY_PAGES = displayPageCount(EXTENDED_INNER_PAGES);

/** "14 pages" / "30 pages" — the bag's length line and the visible cart attribute. */
export function bookLengthLabel(pageCount: BookPageCount): string {
  return `${displayPageCount(pageCount)} pages`;
}

/** How many inner pages a run must end up with. A run without `innerPageCount` predates this
 *  feature (or was bought as Standard) and keeps the global ALL_PAGES_COUNT. */
export function getRunInnerPageCount(config: unknown): number {
  const c = (config ?? {}) as { innerPageCount?: unknown };
  if (Number(c.innerPageCount) === EXTENDED_INNER_PAGES) return EXTENDED_INNER_PAGES;
  const env = typeof process !== 'undefined' ? process.env?.ALL_PAGES_COUNT : undefined;
  return Number(env) || STANDARD_INNER_PAGES;
}

/** Pages 1..N of the standard script that the preview renders and both editions share. */
export function sharedPreviewPages(): number {
  const env = typeof process !== 'undefined' ? process.env?.PREVIEW_PAGES_COUNT : undefined;
  return Math.max(1, Math.min(Number(env) || 3, STANDARD_INNER_PAGES - 1));
}
